import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useRegion } from '../context/RegionContext';
import { FileSearch, Upload, FileText, Mic, StickyNote, Loader2, AlertTriangle,
  Download, Eye, Trash2, ChevronRight, CheckCircle2, XCircle, Clock,
  Plus, ArrowLeft, RefreshCw, FileAudio, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Status Badge ──────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    created: { color: 'bg-slate-500/15 text-slate-400', icon: Clock, label: 'Created' },
    transcribing: { color: 'bg-blue-500/15 text-blue-400', icon: Loader2, label: 'Transcribing' },
    analyzing: { color: 'bg-amber-500/15 text-amber-400', icon: Loader2, label: 'Analyzing' },
    generating: { color: 'bg-purple-500/15 text-purple-400', icon: Loader2, label: 'Generating' },
    creating_document: { color: 'bg-indigo-500/15 text-indigo-400', icon: Loader2, label: 'Creating Doc' },
    gaps_found: { color: 'bg-amber-500/15 text-amber-400', icon: AlertTriangle, label: 'Gaps Found' },
    completed: { color: 'bg-emerald-500/15 text-emerald-400', icon: CheckCircle2, label: 'Completed' },
    error: { color: 'bg-red-500/15 text-red-400', icon: XCircle, label: 'Error' },
  };
  const s = map[status] || map.created;
  const Icon = s.icon;
  const spinning = ['transcribing', 'analyzing', 'generating', 'creating_document'].includes(status);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${s.color}`}>
      <Icon className={`h-3 w-3 ${spinning ? 'animate-spin' : ''}`} />
      {s.label}
    </span>
  );
};

// ─── File Drop Zone ────────────────────────────────────────────────
const FileDropZone = ({ label, accept, multiple, files, setFiles, maxFiles, icon: Icon }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (maxFiles && files.length + dropped.length > maxFiles) return;
    setFiles(prev => multiple ? [...prev, ...dropped].slice(0, maxFiles || 99) : [dropped[0]]);
  }, [files, maxFiles, multiple, setFiles]);

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-2">{label}</label>
      <div
        className={`border-2 border-dashed rounded-xl p-4 text-center transition-colors cursor-pointer
          ${dragOver ? 'border-indigo-400 bg-indigo-50' : 'border-gray-300 hover:border-gray-400 bg-gray-50'}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        data-testid={`dropzone-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <Icon className="h-6 w-6 mx-auto text-gray-400 mb-2" />
        <p className="text-xs text-gray-500">
          {files.length === 0 ? 'Drop file here or click to browse' : `${files.length} file(s) selected`}
        </p>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          className="hidden"
          onChange={(e) => {
            const selected = Array.from(e.target.files);
            setFiles(prev => multiple ? [...prev, ...selected].slice(0, maxFiles || 99) : [selected[0]]);
            e.target.value = '';
          }}
        />
      </div>
      {files.length > 0 && (
        <div className="mt-2 space-y-1">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-2 text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
              <FileAudio className="h-3.5 w-3.5 text-gray-500 shrink-0" />
              <span className="text-gray-700 truncate flex-1">{f.name}</span>
              <span className="text-gray-400">{(f.size / 1024 / 1024).toFixed(1)}MB</span>
              <button onClick={(e) => { e.stopPropagation(); removeFile(i); }} className="text-red-400 hover:text-red-500">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Gap Analysis Modal ────────────────────────────────────────────
const GapModal = ({ gaps, onClose, onForceGenerate, loading }) => {
  if (!gaps) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white border border-amber-200 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6 shadow-xl" data-testid="gap-modal">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="h-6 w-6 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Information Gaps Detected</h3>
            <p className="text-sm text-gray-600 mt-1">{gaps.summary}</p>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs text-gray-500">Coverage Score:</span>
              <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-amber-400 rounded-full" style={{ width: `${gaps.coverage_score || 0}%` }} />
              </div>
              <span className="text-xs font-medium text-amber-600">{gaps.coverage_score || 0}%</span>
            </div>
          </div>
        </div>

        <div className="space-y-3 mb-6">
          <p className="text-sm font-medium text-gray-700">Missing topics — ask the candidate about these:</p>
          {(gaps.missing_topics || []).map((mt, i) => (
            <div key={i} className="bg-gray-50 border border-gray-200 rounded-xl p-3">
              <p className="text-sm font-medium text-gray-900">{mt.topic}</p>
              <p className="text-xs text-gray-600 mt-1">{mt.why_important}</p>
              <div className="mt-2 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">
                <p className="text-xs text-indigo-700">
                  <span className="font-medium">Suggested question:</span> {mt.suggested_question}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors" data-testid="gap-close-btn">
            Go Back & Add More Info
          </button>
          <button onClick={onForceGenerate} disabled={loading}
            className="flex-1 px-4 py-2.5 rounded-xl bg-amber-100 text-amber-800 text-sm font-medium hover:bg-amber-200 transition-colors disabled:opacity-50"
            data-testid="gap-force-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Generate Anyway'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Document Preview ──────────────────────────────────────────────
const DocumentPreview = ({ content }) => {
  if (!content) return null;
  return (
    <div className="space-y-6" data-testid="doc-preview">
      {/* Executive Summary */}
      <div>
        <h3 className="text-base font-semibold text-indigo-600 mb-2">Executive Summary</h3>
        <p className="text-sm text-gray-800 leading-relaxed">{content.executive_summary}</p>
      </div>

      {/* Why Good Fit */}
      {content.why_good_fit?.length > 0 && (
        <div>
          <h3 className="text-base font-semibold text-emerald-600 mb-3">Why This Candidate is a Good Fit</h3>
          <div className="space-y-2">
            {content.why_good_fit.map((r, i) => (
              <div key={i} className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{r.reason || r}</p>
                    {r.details && <p className="text-xs text-gray-600 mt-1">{r.details}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Call Insights */}
      {content.call_insights?.length > 0 && (
        <div>
          <h3 className="text-base font-semibold text-blue-600 mb-3">Call Discussion Insights</h3>
          <div className="space-y-2">
            {content.call_insights.map((ins, i) => (
              <div key={i} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Mic className="h-3.5 w-3.5 text-blue-500" />
                  <span className="text-sm font-medium text-gray-900">{ins.topic}</span>
                  {ins.call_number && <span className="text-[10px] text-gray-500">Call {ins.call_number}</span>}
                </div>
                <p className="text-xs text-gray-700">{ins.key_takeaway}</p>
                {ins.relevance_to_jd && <p className="text-[11px] text-indigo-600 mt-1 italic">{ins.relevance_to_jd}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Skill Alignment */}
      {content.skill_alignment?.length > 0 && (
        <div>
          <h3 className="text-base font-semibold text-purple-600 mb-3">Skill Alignment</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200 bg-gray-50">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">JD Requirement</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Evidence</th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600">Match</th>
                </tr>
              </thead>
              <tbody>
                {content.skill_alignment.map((s, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="py-2 px-3 text-xs text-gray-900 font-medium">{s.jd_requirement}</td>
                    <td className="py-2 px-3 text-xs text-gray-700">{s.candidate_evidence}</td>
                    <td className="py-2 px-3 text-center">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        s.match_level === 'Strong' ? 'bg-emerald-100 text-emerald-700' :
                        s.match_level === 'Moderate' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>{s.match_level}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 10 Impressive Q&A */}
      {content.impressive_qa?.length > 0 && (
        <div>
          <h3 className="text-base font-semibold text-indigo-600 mb-2">Key Questions & Answers for Hiring Manager</h3>
          <p className="text-xs text-gray-500 mb-4 italic">Prepared by our recruitment team based on thorough screening of this candidate.</p>
          <div className="space-y-4">
            {content.impressive_qa.map((qa, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                <p className="text-sm font-semibold text-indigo-700 mb-2">Q{i + 1}. {qa.question}</p>
                <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line">{qa.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Risk Factors */}
      {content.risk_factors?.length > 0 && (
        <div>
          <h3 className="text-base font-semibold text-amber-600 mb-3">Risk Assessment</h3>
          <div className="space-y-2">
            {content.risk_factors.map((r, i) => (
              <div key={i} className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm font-medium text-gray-900">{r.risk}</p>
                {r.mitigation && <p className="text-xs text-emerald-700 mt-1">Mitigation: {r.mitigation}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendation */}
      {content.recommendation && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <h3 className="text-base font-semibold text-emerald-700 mb-2">Recommendation</h3>
          <p className="text-sm text-gray-900 font-medium">{content.recommendation}</p>
          {content.next_steps && <p className="text-xs text-gray-600 mt-2 italic">Next Steps: {content.next_steps}</p>}
        </div>
      )}
    </div>
  );
};


// ─── Main Page ─────────────────────────────────────────────────────
export default function PreScreeningPage() {
  const { region } = useRegion();
  const [view, setView] = useState('list'); // 'list' | 'create' | 'detail'
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [pollInterval, setPollInterval] = useState(null);

  // Form state
  const [jdText, setJdText] = useState('');
  const [resumeText, setResumeText] = useState('');
  const [notes, setNotes] = useState('');
  const [jdFile, setJdFile] = useState([]);
  const [resumeFile, setResumeFile] = useState([]);
  const [callFiles, setCallFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showGapModal, setShowGapModal] = useState(false);
  const [forceLoading, setForceLoading] = useState(false);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/prescreening/list/all${region ? `?region=${region}` : ''}`, { credentials: 'include' });
      if (res.ok) setSessions(await res.json());
    } catch (e) { console.error(e); }
  }, [region]);

  const fetchSession = useCallback(async (id) => {
    try {
      const res = await fetch(`${API}/api/prescreening/${id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setActiveSession(data);
        if (data.status === 'gaps_found') setShowGapModal(true);
        return data;
      }
    } catch (e) { console.error(e); }
    return null;
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  // Polling for in-progress sessions
  useEffect(() => {
    if (!activeSession) return;
    const inProgress = ['transcribing', 'analyzing', 'generating', 'creating_document'].includes(activeSession.status);
    if (!inProgress) {
      if (pollInterval) { clearInterval(pollInterval); setPollInterval(null); }
      return;
    }
    if (pollInterval) return;
    const id = setInterval(async () => {
      const data = await fetchSession(activeSession.id);
      if (data && !['transcribing', 'analyzing', 'generating', 'creating_document'].includes(data.status)) {
        clearInterval(id);
        setPollInterval(null);
        fetchSessions();
      }
    }, 3000);
    setPollInterval(id);
    return () => { clearInterval(id); setPollInterval(null); };
  }, [activeSession?.status, activeSession?.id, fetchSession, fetchSessions]);

  const resetForm = () => {
    setJdText(''); setResumeText(''); setNotes('');
    setJdFile([]); setResumeFile([]); setCallFiles([]);
  };

  const handleCreate = async () => {
    if (!jdText.trim() && jdFile.length === 0) return;
    if (!resumeText.trim() && resumeFile.length === 0) return;

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('jd_text', jdText);
      fd.append('resume_text', resumeText);
      fd.append('notes', notes);
      if (region) fd.append('region', region);
      if (jdFile[0]) fd.append('jd_file', jdFile[0]);
      if (resumeFile[0]) fd.append('resume_file', resumeFile[0]);
      callFiles.forEach(f => fd.append('call_files', f));

      const res = await fetch(`${API}/api/prescreening/create`, {
        method: 'POST', body: fd, credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.detail || 'Failed to create session');
        return;
      }
      const { id } = await res.json();

      // Trigger generation
      await fetch(`${API}/api/prescreening/${id}/generate`, {
        method: 'POST', credentials: 'include',
      });

      resetForm();
      await fetchSession(id);
      setView('detail');
      fetchSessions();
    } catch (e) {
      console.error(e);
      alert('Error creating pre-screening session');
    } finally {
      setSubmitting(false);
    }
  };

  const handleForceGenerate = async () => {
    if (!activeSession) return;
    setForceLoading(true);
    try {
      await fetch(`${API}/api/prescreening/${activeSession.id}/force-generate`, {
        method: 'POST', credentials: 'include',
      });
      setShowGapModal(false);
      await fetchSession(activeSession.id);
    } catch (e) { console.error(e); }
    finally { setForceLoading(false); }
  };

  const handleDownload = async () => {
    if (!activeSession) return;
    try {
      const res = await fetch(`${API}/api/prescreening/${activeSession.id}/download`, { credentials: 'include' });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PreScreening_${activeSession.document_content?.candidate_name || 'Document'}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pre-screening session?')) return;
    await fetch(`${API}/api/prescreening/${id}`, { method: 'DELETE', credentials: 'include' });
    if (activeSession?.id === id) { setActiveSession(null); setView('list'); }
    fetchSessions();
  };

  const openSession = async (id) => {
    setLoading(true);
    await fetchSession(id);
    setView('detail');
    setLoading(false);
  };

  // ─── Render ────────────────────────────────────────────────────────

  const handleRegenerate = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Regenerate this pre-screening document?')) return;
    await fetch(`${API}/api/prescreening/${id}/force-generate`, { method: 'POST', credentials: 'include' });
    fetchSessions();
  };

  // List View
  if (view === 'list') {
    return (
      <div className="space-y-6" data-testid="prescreening-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Pre-Screening Doc AI</h1>
            <p className="text-sm text-muted-foreground mt-1">Generate compelling pre-screening documents for hiring managers</p>
          </div>
          <button
            onClick={() => setView('create')}
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-xl transition-colors"
            data-testid="new-prescreening-btn"
          >
            <Plus className="h-4 w-4" /> New Pre-Screening
          </button>
        </div>

        {sessions.length === 0 ? (
          <div className="text-center py-20 bg-card border rounded-2xl">
            <FileSearch className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium text-foreground">No Pre-Screening Documents Yet</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
              Upload a job description, candidate resume, and call recordings to generate your first pre-screening document.
            </p>
            <button onClick={() => setView('create')} className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-xl transition-colors" data-testid="empty-new-btn">
              Get Started
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {sessions.map(s => (
              <div key={s.id}
                className="flex items-center gap-4 bg-card border rounded-xl px-5 py-4 hover:border-indigo-300 transition-colors cursor-pointer group"
                onClick={() => openSession(s.id)}
                data-testid={`session-row-${s.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <p className="text-sm font-medium text-foreground truncate">{s.candidate_name || 'Processing...'}</p>
                    <StatusBadge status={s.status} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">{s.position || '—'}</p>
                </div>
                {s.coverage_score != null && (
                  <div className="text-center">
                    <p className="text-lg font-bold text-indigo-600">{s.coverage_score}%</p>
                    <p className="text-[10px] text-muted-foreground">Coverage</p>
                  </div>
                )}
                <p className="text-xs text-muted-foreground shrink-0">{s.created_at ? new Date(s.created_at).toLocaleDateString() : ''}</p>
                {s.status === 'completed' && (
                  <button onClick={(e) => handleRegenerate(e, s.id)} title="Regenerate"
                    className="opacity-0 group-hover:opacity-100 text-indigo-500 hover:text-indigo-600 transition-all p-1.5 rounded-lg hover:bg-indigo-50" data-testid={`regen-session-${s.id}`}>
                    <RefreshCw className="h-4 w-4" />
                  </button>
                )}
                <button onClick={(e) => { e.stopPropagation(); handleDelete(s.id); }}
                  className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-500 transition-all p-1.5 rounded-lg hover:bg-red-50" data-testid={`delete-session-${s.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Create View
  if (view === 'create') {
    const canSubmit = (jdText.trim() || jdFile.length > 0) && (resumeText.trim() || resumeFile.length > 0);
    return (
      <div className="space-y-6" data-testid="prescreening-create">
        <div className="flex items-center gap-3">
          <button onClick={() => { setView('list'); resetForm(); }} className="p-2 hover:bg-muted rounded-lg transition-colors" data-testid="back-to-list-btn">
            <ArrowLeft className="h-4 w-4 text-muted-foreground" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">New Pre-Screening Document</h1>
            <p className="text-sm text-muted-foreground">Upload materials to generate a compelling HM document</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: JD + Resume */}
          <div className="space-y-5">
            {/* Job Description */}
            <div className="bg-card border rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="h-4 w-4 text-indigo-600" />
                <h3 className="text-sm font-semibold text-foreground">Job Description</h3>
                <span className="text-[10px] text-red-500 font-medium">REQUIRED</span>
              </div>
              <textarea
                value={jdText}
                onChange={(e) => setJdText(e.target.value)}
                placeholder="Paste the complete job description here..."
                className="w-full h-40 bg-background border rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground resize-none focus:outline-none focus:border-indigo-500 transition-colors"
                data-testid="jd-textarea"
              />
              <div className="mt-2">
                <FileDropZone label="Or upload JD file (PDF/Word)" accept=".pdf,.doc,.docx,.txt"
                  files={jdFile} setFiles={setJdFile} icon={Upload} />
              </div>
            </div>

            {/* Resume */}
            <div className="bg-card border rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="h-4 w-4 text-emerald-600" />
                <h3 className="text-sm font-semibold text-foreground">Candidate Resume</h3>
                <span className="text-[10px] text-red-500 font-medium">REQUIRED</span>
              </div>
              <textarea
                value={resumeText}
                onChange={(e) => setResumeText(e.target.value)}
                placeholder="Paste the candidate's resume content here..."
                className="w-full h-40 bg-background border rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground resize-none focus:outline-none focus:border-indigo-500 transition-colors"
                data-testid="resume-textarea"
              />
              <div className="mt-2">
                <FileDropZone label="Or upload resume (PDF/Word)" accept=".pdf,.doc,.docx,.txt"
                  files={resumeFile} setFiles={setResumeFile} icon={Upload} />
              </div>
            </div>
          </div>

          {/* Right: Calls + Notes */}
          <div className="space-y-5">
            {/* Call Recordings */}
            <div className="bg-card border rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Mic className="h-4 w-4 text-blue-600" />
                <h3 className="text-sm font-semibold text-foreground">Call Recordings</h3>
                <span className="text-[10px] text-muted-foreground font-medium">UP TO 5</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Upload recruiter & manager call recordings with the candidate. Supported: MP3, WAV, M4A, AAC, OGG, FLAC, WebM (max 25MB each).
              </p>
              <FileDropZone
                label="Drop call recordings here"
                accept=".mp3,.wav,.m4a,.aac,.ogg,.flac,.webm,.mp4,.mpeg"
                multiple={true}
                maxFiles={5}
                files={callFiles}
                setFiles={setCallFiles}
                icon={Mic}
              />
            </div>

            {/* Additional Notes */}
            <div className="bg-card border rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <StickyNote className="h-4 w-4 text-amber-500" />
                <h3 className="text-sm font-semibold text-foreground">Additional Notes</h3>
                <span className="text-[10px] text-muted-foreground font-medium">OPTIONAL</span>
              </div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional context — recruiter notes, hiring preferences, special requirements..."
                className="w-full h-32 bg-background border rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground resize-none focus:outline-none focus:border-indigo-500 transition-colors"
                data-testid="notes-textarea"
              />
            </div>

            {/* Info Box */}
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
              <p className="text-xs text-indigo-700 leading-relaxed">
                <span className="font-semibold">How it works:</span> The AI will transcribe your call recordings,
                analyze the JD and resume, cross-reference all information, and generate a comprehensive
                pre-screening document with 10 impressive Q&A pairs for the hiring manager. If critical
                information is missing, you'll see a gap analysis with suggested follow-up questions.
              </p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            onClick={handleCreate}
            disabled={!canSubmit || submitting}
            className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-300 disabled:text-gray-500 text-white text-sm font-semibold rounded-xl transition-colors"
            data-testid="generate-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSearch className="h-4 w-4" />}
            {submitting ? 'Processing...' : 'Generate Pre-Screening Document'}
          </button>
        </div>
      </div>
    );
  }

  // Detail View
  if (view === 'detail' && activeSession) {
    const inProgress = ['transcribing', 'analyzing', 'generating', 'creating_document'].includes(activeSession.status);
    return (
      <div className="space-y-6" data-testid="prescreening-detail">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => { setView('list'); setActiveSession(null); }} className="p-2 hover:bg-muted rounded-lg transition-colors" data-testid="back-btn">
              <ArrowLeft className="h-4 w-4 text-muted-foreground" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-foreground">
                  {activeSession.document_content?.candidate_name || 'Pre-Screening Document'}
                </h1>
                <StatusBadge status={activeSession.status} />
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {activeSession.document_content?.position_applied || activeSession.progress}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {activeSession.status === 'completed' && (
              <>
                <button onClick={handleDownload}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-xl transition-colors"
                  data-testid="download-btn">
                  <Download className="h-4 w-4" /> Download .docx
                </button>
                <button onClick={async () => {
                  await fetch(`${API}/api/prescreening/${activeSession.id}/force-generate`, {
                    method: 'POST', credentials: 'include',
                  });
                  fetchSession(activeSession.id);
                }}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors"
                  data-testid="regenerate-detail-btn">
                  <RefreshCw className="h-4 w-4" /> Regenerate
                </button>
              </>
            )}
            {activeSession.status === 'gaps_found' && (
              <button onClick={() => setShowGapModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 border border-amber-200 text-sm font-medium rounded-xl hover:bg-amber-100 transition-colors"
                data-testid="view-gaps-btn">
                <AlertTriangle className="h-4 w-4" /> View Gaps
              </button>
            )}
            {(activeSession.status === 'error' || activeSession.status === 'gaps_found') && (
              <button onClick={async () => {
                await fetch(`${API}/api/prescreening/${activeSession.id}/force-generate`, {
                  method: 'POST', credentials: 'include',
                });
                fetchSession(activeSession.id);
              }}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors"
                data-testid="retry-btn">
                <RefreshCw className="h-4 w-4" /> Retry
              </button>
            )}
          </div>
        </div>

        {/* Progress indicator */}
        {inProgress && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-6 text-center">
            <Loader2 className="h-10 w-10 animate-spin text-indigo-600 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-900">{activeSession.progress || 'Processing...'}</p>
            <p className="text-xs text-gray-500 mt-1">This may take 1-3 minutes depending on the number of call recordings</p>

            {/* Transcription progress */}
            {activeSession.call_transcripts && Object.keys(activeSession.call_transcripts).length > 0 && (
              <div className="mt-4 space-y-1">
                {Object.entries(activeSession.call_transcripts).map(([idx, t]) => (
                  <div key={idx} className="flex items-center gap-2 justify-center">
                    <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                    <span className="text-xs text-gray-600">Call {parseInt(idx) + 1} transcribed</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {activeSession.status === 'error' && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
            <div className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-500" />
              <p className="text-sm text-red-700">{activeSession.error || 'An error occurred during generation'}</p>
            </div>
          </div>
        )}

        {/* Document Preview */}
        {activeSession.status === 'completed' && activeSession.document_content && (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-5">
              <Eye className="h-4 w-4 text-indigo-600" />
              <h2 className="text-base font-semibold text-gray-900">Document Preview</h2>
            </div>
            <DocumentPreview content={activeSession.document_content} />
          </div>
        )}

        {/* Call Transcripts */}
        {activeSession.call_transcripts && Object.keys(activeSession.call_transcripts).length > 0 && (
          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Call Transcripts</h2>
            <div className="space-y-3">
              {Object.entries(activeSession.call_transcripts).map(([idx, transcript]) => (
                <details key={idx} className="group">
                  <summary className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 hover:text-gray-900 transition-colors">
                    <FileAudio className="h-4 w-4 text-blue-500" />
                    <span>Call {parseInt(idx) + 1} Transcript</span>
                    <ChevronRight className="h-3 w-3 text-gray-400 group-open:rotate-90 transition-transform" />
                  </summary>
                  <div className="mt-2 ml-6 bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                    <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">{transcript}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        )}

        {/* Gap Modal */}
        {showGapModal && activeSession.gap_analysis && (
          <GapModal
            gaps={activeSession.gap_analysis}
            onClose={() => setShowGapModal(false)}
            onForceGenerate={handleForceGenerate}
            loading={forceLoading}
          />
        )}
      </div>
    );
  }

  return null;
}
